import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ReplayIcon from '@mui/icons-material/Replay';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import useVoyagerStore from '../../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../../stores/slices/landingAgentSlice';
import { followUpOf } from '../../../stores/slices/landingAgentSlice';
import TraceLink from '../../agent-common/TraceLink';
import { answerPivots, rowHoldsTrace } from '../AnswerCard';
import { useRouteChange } from '../RouteChip';
import { useScopeChange } from '../ScopeChip';
import { useTrackObjective } from '../ObjectiveLine';
import { ShareButton } from '../../../sharing/Sharing';

const ACT = {
  height: 30, minWidth: 30, px: 0.75, borderRadius: '7px', gap: 0.75, fontSize: 13, color: 'var(--ink-3)',
  '&:hover': { bgcolor: 'var(--hover, var(--surface2))', color: 'var(--ink)' },
  '&[aria-expanded="true"]': { bgcolor: 'var(--active, var(--hover, var(--surface2)))', color: 'var(--ink)' },
  '&.Mui-disabled': { opacity: 0.4 },
} as const;

const LINK = {
  font: 'inherit', fontSize: 14, color: 'var(--ink)', p: 0, borderRadius: '2px', textAlign: 'left',
  textDecoration: 'underline', textDecorationColor: 'var(--line)', textUnderlineOffset: '4px', textDecorationThickness: '1px',
  transition: 'text-decoration-color .15s', '&:hover, &:focus-visible': { textDecorationColor: 'var(--ink)' },
} as const;

/** A follow-up as its link reads under "Break it down": a leading "Show " dropped (the click sends the whole question). */
export function linkWords(followUp: string): string {
  const rest = followUp.replace(/^show\s+/i, '');
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : followUp;
}

/** The synthesis follow-ups, each asked about THIS answer (`followUpOf`): one line, "Break it down", then each a link. */
export function FollowUpChips({ turn }: { turn: LandingAgentTurn }) {
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const result = turn.result;
  const followUps: string[] = result?.synthesis?.follow_ups || [];
  // as the card's list: none on an in-range answer, and none on a walk
  const normal = (result?.ladder as any)?.context?.significance?.kind === 'within_normal';
  const listed = normal || result?.diagnose ? [] : followUps;
  if (listed.length === 0) return null;
  return (
    // one run of text: a separator is held to the link before it (a no-break space), so a wrapped line starts with a
    // link, never with a stray dot
    <Box component="nav" aria-label="Ask next" data-testid="follow-up-chips"
      sx={{ display: 'block', fontSize: 14, lineHeight: 1.7, color: 'var(--ink-3)' }}>
      <Box component="span" sx={{ mr: 1.25 }}>Break it down</Box>{' '}
      {listed.map((f, i) => (
        <React.Fragment key={i}>
          <ButtonBase onClick={() => send(f, followUpOf(turn))} title={f} sx={{ ...LINK, display: 'inline', verticalAlign: 'baseline' }}>{linkWords(f)}</ButtonBase>
          {i < listed.length - 1 && <><Box component="span" aria-hidden sx={{ color: 'var(--ink-4, var(--line))', mx: 1 }}>{' ·'}</Box>{' '}</>}
        </React.Fragment>
      ))}
    </Box>
  );
}

type MoreView = 'main' | 'route' | 'scope';

const HEAD = { lineHeight: '30px', fontSize: 11.5, color: 'var(--ink-3)', bgcolor: 'transparent' } as const;
const VALUE = { ml: 'auto', pl: 3, color: 'var(--ink-3)', fontSize: 13, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/**
 * More ⋯: "Why" and What's next (the card's buttons with the flag off, #1587), and — with `answer` (a data answer's
 * clean row) — the answer's own controls, moved out of the main view: Answer it another way (the route's Change), Use
 * another perspective (the scope's Change), Track as an objective, and Share this conversation. Each is the same action
 * as on the line it came from.
 */
function More({ turn, answer }: { turn: LandingAgentTurn; answer: boolean }) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [view, setView] = React.useState<MoreView>('main');
  const [sharing, setSharing] = React.useState(false);
  const pivots = answerPivots(turn, turn.result);
  const routing = useRouteChange(turn);
  const scoping = useScopeChange(turn);
  const track = useTrackObjective(turn);
  const threadId = useVoyagerStore((s) => s.currentThreadId);
  // said even where it cannot be changed (an answer with no recorded request): the item is there, disabled
  const route = answer ? routing : null;
  const scope = answer ? scoping : null;
  const tracking = answer ? track : null;
  const share = answer && !!threadId;
  if (!pivots && !route && !scope && !tracking && !share) return null;
  const close = () => { setAnchor(null); setView('main'); };
  const pick = (act: () => void) => () => { close(); act(); };
  const back = (
    <MenuItem key="back" onClick={() => setView('main')} sx={{ color: 'var(--ink-3)', fontSize: 13 }}>
      <ChevronLeftIcon sx={{ fontSize: 16, mr: 0.5 }} />Back
    </MenuItem>
  );
  const items = view === 'route' && route ? [
    back,
    <ListSubheader key="h" sx={HEAD} data-testid="more-route-reason">
      {`Answered as ${route.label}${route.route.reason ? `: ${route.route.reason}` : ''}`}
    </ListSubheader>,
    ...route.choices.map((c) => <MenuItem key={c.to} onClick={pick(() => route.change(c.to))}>{c.label}</MenuItem>),
  ] : view === 'scope' && scope ? [
    back,
    <ListSubheader key="h" sx={HEAD}>{`Answered from ${scope.label}`}</ListSubheader>,
    ...scope.choices.map((c) => <MenuItem key={c.id} onClick={pick(() => scope.change(c.id))}>{c.name}</MenuItem>),
  ] : [
    ...(pivots?.why ? [<MenuItem key="why" onClick={pick(pivots.why.ask)} data-testid="pivot-why">{pivots.why.label}</MenuItem>] : []),
    ...(pivots ? [<MenuItem key="next" onClick={pick(pivots.next.ask)} disabled={pivots.next.disabled} data-testid="pivot-next">What&apos;s next</MenuItem>] : []),
    ...(route ? [<MenuItem key="route" onClick={() => setView('route')} disabled={!route.choices.length} data-testid="more-route">
      Answer it another way<Box component="span" data-testid="more-route-label" sx={VALUE}>{route.label}</Box></MenuItem>] : []),
    ...(scope ? [<MenuItem key="scope" onClick={() => setView('scope')} disabled={!scope.choices.length} data-testid="more-scope">
      Use another perspective<Box component="span" data-testid="more-scope-label" sx={VALUE}>{scope.label}</Box></MenuItem>] : []),
    ...(tracking ? [<MenuItem key="track" onClick={pick(tracking)} data-testid="track-objective">Track as an objective</MenuItem>] : []),
    ...(share ? [<MenuItem key="share" onClick={pick(() => setSharing(true))} data-testid="more-share">Share this conversation</MenuItem>] : []),
  ];
  return (
    <>
      <Tooltip title="More" arrow>
        <ButtonBase aria-label="More" aria-haspopup="menu" aria-expanded={!!anchor} onClick={(e) => setAnchor(e.currentTarget)} sx={ACT} data-testid="answer-more">
          <MoreHorizIcon sx={{ fontSize: 16 }} />
        </ButtonBase>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close} MenuListProps={{ sx: { minWidth: 240 } }}>
        {items}
      </Menu>
      {share && sharing && <ShareButton threadId={threadId as string} dialog={{ open: sharing, onClose: () => setSharing(false) }} />}
    </>
  );
}

/**
 * The icons in the answer's row (#1506, flag on): copy, thumbs up/down, ask again, Correct this, then More (Why, What's
 * next and, on a data answer, its own controls). Copy takes the answer as it reads on screen (a provisional banner
 * included). The thumbs are today's feedback; with `onBad` the thumbs down opens today's "What went wrong?" form instead.
 * Ask again re-sends the turn's own request (`askedRequest`), exactly as Change does with its route left alone; a walk
 * opened from a number (which records no ask) runs the same walk again; any other answer without one (from before #760)
 * offers none rather than guess its scope. Correct this opens a walk's verdict correction (#186), or, on a data answer
 * (#1508), the thread's composer prefilled to reply to that answer. Trace is today's link, for an answer whose row has
 * no Trace of its own.
 */
export default function AnswerActions({ turn, answerRef, onCorrect, onBad, answer = false }: {
  turn: LandingAgentTurn;
  answerRef: React.RefObject<HTMLElement | null>;
  onCorrect?: () => void;
  /** the thumbs down opens the feedback form (rating it is the form's Submit) */
  onBad?: () => void;
  /** a data answer's clean row: More carries the route, scope, objective and share controls */
  answer?: boolean;
}) {
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const retryWalk = useVoyagerStore((s) => s.retryWalk);
  const submit = useVoyagerStore((s) => s.submitTurnFeedback);
  const clear = useVoyagerStore((s) => s.clearTurnFeedback);
  const [copied, setCopied] = React.useState(false);
  if (!['done', 'error', 'cancelled'].includes(turn.status)) return null;
  const reask = turn.askedRequest;
  // a walk opened from a number records no ask (#184), live or reopened (a replay carries no walk marker): its retry is
  // the same walk again, from its envelope
  const walkAgain = !reask && !!turn.result?.diagnose?.target?.metric;
  const rating = turn.feedback?.rating || null;
  const rate = (r: 'up' | 'down') => {
    if (rating === r) clear(turn.id);
    else if (r === 'down' && onBad) onBad();
    else submit(turn.id, { rating: r, tags: [], submittedAt: Date.now() });
  };
  const copy = async () => {
    // the answer's wrapper has no box of its own (display: contents), so its cards are read one by one
    // the result card (#1507) is a way into the panel, not part of the answer, so it is left out
    const cards = (Array.from(answerRef.current?.children || []) as HTMLElement[]).filter((c) => !c.hasAttribute('data-no-copy'));
    const text = cards.map((c) => (c.innerText ?? c.textContent ?? '').trim()).filter(Boolean).join('\n\n');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // best-effort, as the card's copy buttons
    }
  };
  return (
    <Box data-testid="answer-actions" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
      <Tooltip title={copied ? 'Copied' : 'Copy'} arrow>
        <ButtonBase aria-label="Copy" onClick={copy} sx={ACT}>
          {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </ButtonBase>
      </Tooltip>
      {turn.sessionId && <>
        <ButtonBase aria-label="Good answer" aria-pressed={rating === 'up'} onClick={() => rate('up')} sx={ACT}>
          {rating === 'up' ? <ThumbUpAltIcon sx={{ fontSize: 16, color: 'success.main' }} /> : <ThumbUpAltOutlinedIcon sx={{ fontSize: 16 }} />}
        </ButtonBase>
        <ButtonBase aria-label="Bad answer" aria-pressed={rating === 'down'} onClick={() => rate('down')} sx={ACT}>
          {rating === 'down' ? <ThumbDownAltIcon sx={{ fontSize: 16, color: 'error.main' }} /> : <ThumbDownAltOutlinedIcon sx={{ fontSize: 16 }} />}
        </ButtonBase>
      </>}
      {(reask || walkAgain) && (
        <Tooltip title="Ask again" arrow>
          <ButtonBase aria-label="Ask again" onClick={() => (reask ? send(reask.query, { reask }) : retryWalk(turn.id))} sx={ACT}>
            <ReplayIcon sx={{ fontSize: 16 }} />
          </ButtonBase>
        </Tooltip>
      )}
      {onCorrect && (
        <Tooltip title="Correct this" arrow>
          <ButtonBase aria-label="Correct this" onClick={onCorrect} sx={ACT} data-testid="correct-this">
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </ButtonBase>
        </Tooltip>
      )}
      <More turn={turn} answer={answer} />
      {turn.sessionId && !rowHoldsTrace(turn) && <TraceLink sessionId={turn.sessionId} />}
    </Box>
  );
}
